import React, { forwardRef } from 'react';
import './Button.css';

/**
 * Modern Button Component
 * 
 * @param {string} variant - 'primary', 'secondary', 'outline', 'ghost', 'danger', 'glass'
 * @param {string} size - 'sm', 'md', 'lg'
 * @param {boolean} isLoading - Shows a loading spinner
 * @param {React.ReactNode} leftIcon - Icon to display before text
 * @param {React.ReactNode} rightIcon - Icon to display after text
 * @param {boolean} fullWidth - Whether the button takes full width
 */
const Button = forwardRef(({ 
  children, 
  className = '', 
  variant = 'primary', 
  size = 'md', 
  isLoading = false, 
  disabled = false, 
  leftIcon, 
  rightIcon, 
  fullWidth = false,
  type = 'button',
  href,
  ...props 
}, ref) => {
  const baseClass = 'ui-btn';
  const classes = [
    baseClass,
    `${baseClass}--${variant}`,
    `${baseClass}--${size}`,
    fullWidth ? `${baseClass}--block` : '',
    isLoading ? `${baseClass}--loading` : '',
    className
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {isLoading && (
        <span className="ui-btn__spinner">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      )}
      
      {!isLoading && leftIcon && <span className="ui-btn__icon-left">{leftIcon}</span>}
      <span className="ui-btn__content">{children}</span>
      {!isLoading && rightIcon && <span className="ui-btn__icon-right">{rightIcon}</span>}
    </>
  );

  if (href) {
    return (
      <a
        ref={ref}
        href={href}
        className={classes}
        aria-disabled={disabled || isLoading}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button 
      ref={ref}
      type={type} 
      className={classes} 
      disabled={disabled || isLoading}
      {...props}
    >
      {content}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
